import * as React from "react";
import { useEffect, useRef } from "react";
import { Checkbox, Dropdown, Input } from "semantic-ui-react";
import { useFormContext, useWatch } from "react-hook-form";
import { AlertTriangle } from "lucide-react";
import { PolymorphicPlayerCard, PlayerCardReview, PlayerCardReviewDisplayType } from "@/services/bedrock";
import { Alert } from "@/components/ui/alert";
import PlayerCardField from "./Fields/PlayerCardField";
import ReviewSelector from "../../../ReviewSelector";
import ReviewSourcesSelect from "../../../ReviewSourcesSelect";
import { REVIEW_DISPLAY_TYPE_LABELS, REVIEW_MIN_SCORE_SETTINGS } from "../../../utils";
import { useReviewWidgetPreview } from "../../../reviewWidgetPreview";

interface ReviewWidgetSettingsProps {
  index: number;
}

const ReviewWidgetSettings: React.FC<ReviewWidgetSettingsProps> = ({ index }) => {
  const { control, getValues, setValue } = useFormContext<PolymorphicPlayerCard>();
  const owner = `widgets.${index}.owner` as const;
  const review = useWatch({ control, name: owner }) as PlayerCardReview;
  const displayType = review.displayType;
  const isTemplate = getValues("isTemplate");
  const playerCardId = getValues("id");
  const playerId = isTemplate ? undefined : getValues("playerId");
  const minScoreSetting = REVIEW_MIN_SCORE_SETTINGS[displayType];

  const { available } = useReviewWidgetPreview(playerCardId, isTemplate, review);

  const previousDisplayType = useRef(displayType);
  useEffect(() => {
    if (previousDisplayType.current === displayType) return;
    previousDisplayType.current = displayType;
    setValue(`${owner}.minScore`, minScoreSetting?.defaultValue ?? null, { shouldDirty: true });
  }, [displayType]);

  const displayTypeOptions = (Object.keys(REVIEW_DISPLAY_TYPE_LABELS) as PlayerCardReviewDisplayType[])
    .filter(type => !isTemplate || type !== "SPECIFIC_REVIEW")
    .map(type => ({ text: REVIEW_DISPLAY_TYPE_LABELS[type], value: type }));

  return (
    <div className="playerCardWidgetOwner playerCardReview">
      {available === false && (
        <Alert variant="warning">
          <AlertTriangle />
          <Alert.Description>
            Reputation management is not enabled for this Player, so this widget will not be displayed.
          </Alert.Description>
        </Alert>
      )}
      <PlayerCardField
        index={index}
        fieldName="displayType"
        fieldLabel="Display"
        rules={{ required: "This is required." }}
        component={Dropdown}
        placeholder="Display"
        selection
        options={displayTypeOptions}
      />
      {displayType === "SPECIFIC_REVIEW" && (
        <PlayerCardField
          index={index}
          fieldName="reviewId"
          fieldLabel="Review"
          rules={{ required: "This is required." }}
          component={ReviewSelector}
          playerId={playerId}
          selectedReview={getValues(`${owner}.review`)}
        />
      )}
      {displayType === "FEATURED_REVIEWS" && (
        <>
          <PlayerCardField
            index={index}
            fieldName="sourceSystems"
            fieldLabel="Review sources"
            component={ReviewSourcesSelect}
          />
          <PlayerCardField
            index={index}
            fieldName="autoScrollEnabled"
            fieldLabel="Auto scroll through reviews"
            component={Checkbox}
            toggle
          />
        </>
      )}
      {(displayType === "FEATURED_REVIEWS" || displayType === "SPECIFIC_REVIEW") && (
        <PlayerCardField
          index={index}
          fieldName="useCondensedQuotes"
          fieldLabel="Show condensed quote"
          component={Checkbox}
          toggle
        />
      )}
      {minScoreSetting && (
        <>
          <PlayerCardField
            index={index}
            fieldName="minScore"
            fieldLabel="Minimum score"
            rules={{
              required: "This is required.",
              min: { value: minScoreSetting.min, message: `Must be at least ${minScoreSetting.min}.` },
              max: { value: minScoreSetting.max, message: `Must be at most ${minScoreSetting.max}.` },
            }}
            component={Input}
            type="number"
            min={minScoreSetting.min}
            max={minScoreSetting.max}
            step={minScoreSetting.step}
          />
          <p className="-mt-2 mb-3 text-xs text-muted-foreground">
            Hidden when the score is below the minimum or there are fewer than 5 responses.
          </p>
        </>
      )}
      <PlayerCardField
        index={index}
        fieldName="linkToReviewPage"
        fieldLabel="Link to reviews page"
        component={Checkbox}
        toggle
      />
      <p className="-mt-2 mb-3 text-xs text-muted-foreground">
        Lets visitors open the reviews page from the rating or from a review they tap.
      </p>
    </div>
  );
};
export default ReviewWidgetSettings;
